// SVGs loaded into <img> via blob URL run in a sandboxed document that doesn't
// inherit the parent page's @font-face rules. Without explicit font embedding,
// rasterized stickers fall back to the OS default sans-serif and lose the
// Mukta Mahee Bold look that defines the brand.
//
// Strategy: fetch the Mukta Mahee WOFF2 file once, base64-encode it, and
// inject a <style> with an inline @font-face at the top of every serialized
// SVG before we hand it to the canvas rasterizer.

const FONT_URL = "https://fonts.gstatic.com/s/muktamahee/v17/XRXQ3IOIi0hcP8iVU67hA9MFcBoHJndqZCsW.woff2";

let fontPromise: Promise<string> | null = null;

async function fetchFontBase64(): Promise<string> {
    const res = await fetch(FONT_URL);
    if (!res.ok) throw new Error(`Font fetch failed: ${res.status}`);
    const buf = await res.arrayBuffer();
    return arrayBufferToBase64(buf);
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(
            null,
            bytes.subarray(i, i + chunkSize) as unknown as number[],
        );
    }
    return btoa(binary);
}

export async function getFontFaceStyleBlock(): Promise<string> {
    if (!fontPromise) fontPromise = fetchFontBase64();
    try {
        const base64 = await fontPromise;
        return `
            <style>
                @font-face {
                    font-family: 'Mukta Mahee';
                    font-style: normal;
                    font-weight: 700;
                    src: url(data:font/woff2;base64,${base64}) format('woff2');
                }
            </style>
        `;
    } catch (e) {
        // If the font can't be fetched (offline, blocked), fall through with
        // an empty block — stickers will render in the system fallback. Don't
        // poison the cache; next call retries.
        fontPromise = null;
        console.warn("Mukta Mahee font embed failed; falling back to system sans", e);
        return "";
    }
}

// Inject the font @font-face block into a serialized SVG string, immediately
// after the opening <svg ...> tag.
export function injectFontFaceIntoSvg(svgMarkup: string, fontFaceBlock: string): string {
    if (!fontFaceBlock) return svgMarkup;
    return svgMarkup.replace(
        /<svg(\s[^>]*)?>/,
        (match) => `${match}<defs>${fontFaceBlock}</defs>`,
    );
}
